// File Name: WhyChoose.jsx

import React from "react";

import { motion } from "framer-motion";

import { CheckCircle2 } from "lucide-react";

import { WHY_CHOOSE } from "@/lib/site";

export default function WhyChoose() {
  return (
    <>
      {/* PREMIUM MOBILE RESPONSIVE STYLES */}

      <style>
        {`
          @keyframes whyFloat {

            0%,
            100% {
              transform:
                translateY(0px);
            }

            50% {
              transform:
                translateY(-8px);
            }
          }

          @keyframes whyGlow {

            0%,
            100% {
              opacity: 0.4;
            }

            50% {
              opacity: 0.9;
            }
          }

          @keyframes whyPulse {

            0%,
            100% {
              transform: scale(1);
            }

            50% {
              transform: scale(1.05);
            }
          }

          .why-card {

            animation:
              whyFloat 6s ease-in-out infinite;
          }

          .why-highlight {

            animation:
              whyGlow 5s ease-in-out infinite;
          }

          .why-icon {

            animation:
              whyPulse 4s ease-in-out infinite;
          }

          @media (max-width: 1024px) {

            .why-layout {

              gap: 4rem !important;
            }
          }

          @media (max-width: 768px) {

            .why-section {

              padding-top: 6rem !important;

              padding-bottom: 6rem !important;
            }

            .why-container {

              padding-left: 1.25rem !important;

              padding-right: 1.25rem !important;
            }

            .why-heading {

              font-size: 2.4rem !important;

              line-height: 1.15 !important;
            }

            .why-description {

              font-size: 1rem !important;

              line-height: 1.9 !important;
            }

            .why-grid {

              gap: 1.25rem !important;
            }

            .why-card {

              padding: 1.6rem !important;

              border-radius: 28px !important;

              animation:
                whyFloat 5s ease-in-out infinite;
            }

            .why-card:hover {

              transform:
                translateY(-5px)
                scale(1.01) !important;
            }

            .why-icon {

              width: 56px !important;

              height: 56px !important;

              border-radius: 18px !important;
            }

            .why-title {

              font-size: 1.35rem !important;

              line-height: 1.35 !important;
            }

            .why-text {

              font-size: 0.94rem !important;

              line-height: 1.8 !important;
            }

            .why-top-glow {

              width: 520px !important;

              height: 520px !important;

              filter:
                blur(110px) !important;
            }

            .why-side-glow {

              width: 260px !important;

              height: 260px !important;

              filter:
                blur(90px) !important;
            }
          }

          @media (max-width: 480px) {

            .why-heading {

              font-size: 2rem !important;
            }

            .why-card {

              padding: 1.4rem !important;
            }

            .why-title {

              font-size: 1.2rem !important;
            }

            .why-text {

              font-size: 0.88rem !important;
            }

            .why-icon {

              width: 52px !important;

              height: 52px !important;
            }
          }
        `}
      </style>

      <section
        className="
          why-section

          relative

          py-28
          md:py-32

          overflow-hidden

          bg-gradient-to-b
          from-[#F8F4FB]
          via-white
          to-[#F4EEFA]
        "
        data-testid="why-section"
      >

        {/* TOP GLOW */}

        <div
          className="
            why-top-glow

            absolute

            -top-40
            left-1/2

            -translate-x-1/2

            w-[850px]
            h-[850px]

            bg-purple-300/15

            blur-[160px]

            rounded-full
          "
        />

        {/* SIDE GLOW */}

        <div
          className="
            why-side-glow

            absolute

            right-[-120px]
            bottom-[-120px]

            w-[400px]
            h-[400px]

            bg-fuchsia-300/10

            blur-[120px]

            rounded-full
          "
        />

        {/* GRID BG */}

        <div
          className="
            absolute
            inset-0

            grid-bg

            opacity-[0.03]
          "
        />

        <div
          className="
            why-container

            relative

            max-w-7xl
            mx-auto

            px-6
            md:px-8
          "
        >

          <div
            className="
              why-layout

              grid
              grid-cols-1
              lg:grid-cols-12

              gap-16

              items-start
            "
          >

            {/* LEFT CONTENT */}

            <div className="lg:col-span-5">

              {/* LABEL */}

              <div
                className="
                  text-sm

                  uppercase

                  tracking-[0.35em]

                  text-purple-600

                  font-bold

                  mb-5
                "
              >

                WHY CHOOSE US

              </div>

              {/* HEADING */}

              <h2
                className="
                  why-heading

                  text-4xl
                  md:text-5xl

                  font-bold

                  tracking-tight

                  text-slate-900

                  leading-tight

                  mb-7
                "
                style={{
                  fontFamily: "Sora",
                }}
              >

                Built for businesses
                <br />

                <span
                  className="
                    relative

                    inline-block

                    text-purple-700
                  "
                >

                  scaling with technology

                  <span
                    className="
                      why-highlight

                      absolute

                      inset-x-0
                      bottom-2

                      h-5

                      bg-purple-300/40

                      blur-2xl

                      -z-10
                    "
                  />

                </span>

              </h2>

              {/* DESCRIPTION */}

              <p
                className="
                  why-description

                  text-slate-600

                  text-lg

                  leading-relaxed
                "
              >

                From enterprise engineering and
                AI-powered systems to global staffing
                and cloud infrastructure, we help
                organizations innovate faster with
                scalable digital solutions and elite
                technical expertise.

              </p>

            </div>

            {/* RIGHT GRID */}

            <div
              className="
                why-grid

                lg:col-span-7

                grid
                grid-cols-1
                md:grid-cols-2

                gap-7
              "
            >

              {WHY_CHOOSE.map((w, i) => (

                <motion.div
                  key={w.title}

                  initial={{
                    opacity: 0,
                    y: 25,
                  }}

                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}

                  viewport={{
                    once: true,
                  }}

                  transition={{
                    duration: 0.55,
                    delay: i * 0.08,
                  }}

                  className="
                    why-card

                    group

                    relative

                    overflow-hidden

                    rounded-[32px]

                    border
                    border-purple-100/60

                    bg-white/70

                    backdrop-blur-2xl

                    p-8

                    shadow-[0_10px_40px_rgba(120,70,180,0.08)]

                    hover:shadow-[0_30px_70px_rgba(120,70,180,0.16)]

                    hover:-translate-y-3

                    transition-all
                    duration-500
                  "
                >

                  {/* CARD GLOW */}

                  <div
                    className="
                      absolute

                      top-0
                      right-0

                      w-40
                      h-40

                      bg-purple-300/10

                      rounded-full

                      blur-3xl

                      opacity-0

                      group-hover:opacity-100

                      transition-all
                      duration-500
                    "
                  />

                  {/* ICON */}

                  <div
                    className="
                      why-icon

                      relative

                      w-14
                      h-14

                      rounded-2xl

                      bg-gradient-to-br
                      from-[#6D3B8C]
                      via-[#8B5CF6]
                      to-[#C084FC]

                      flex
                      items-center
                      justify-center

                      mb-6

                      shadow-lg
                      shadow-purple-500/20

                      group-hover:scale-110

                      transition-all
                      duration-500
                    "
                  >

                    <CheckCircle2
                      className="
                        w-6
                        h-6

                        text-white
                      "
                    />

                  </div>

                  {/* TITLE */}

                  <h3
                    className="
                      why-title

                      text-2xl

                      font-bold

                      text-slate-900

                      mb-3
                    "
                    style={{
                      fontFamily: "Sora",
                    }}
                  >

                    {w.title}

                  </h3>

                  {/* DESCRIPTION */}

                  <p
                    className="
                      why-text

                      text-slate-600

                      leading-relaxed

                      text-sm
                    "
                  >

                    {w.desc}

                  </p>

                </motion.div>

              ))}

            </div>

          </div>

        </div>

      </section>
    </>
  );
}